import { useMemo, useState } from 'react';
import { CheckCircle2, ClipboardCopy } from 'lucide-react';
import { ToolHeader } from '../components/ToolHeader';
import { dedupeLines } from '../lib/dedupeLines';

const SAMPLE_INPUT = [
  'foo',
  '  foo',
  'BAR',
  'bar',
  'baz',
  'foo',
  'baz',
  '',
  '',
  'qux',
].join('\n');

/**
 * `/tools/convert/dedupe-lines` — line deduplicator.
 *
 * Default behaviour mirrors `Set` semantics: keep the first
 * occurrence, exact-match. Toggles let users normalise (trim,
 * caseInsensitive), choose first-vs-last winner, or switch to a
 * `uniq -c`-style counted output.
 */
export default function DedupeLinesTool(): JSX.Element {
  const [input, setInput] = useState<string>(SAMPLE_INPUT);
  const [caseInsensitive, setCaseInsensitive] = useState(false);
  const [trim, setTrim] = useState(false);
  const [keepFirst, setKeepFirst] = useState(true);
  const [emitCount, setEmitCount] = useState(false);
  const [copied, setCopied] = useState(false);

  const out = useMemo(
    () => dedupeLines(input, { caseInsensitive, trim, keepFirst, emitCount }),
    [input, caseInsensitive, trim, keepFirst, emitCount],
  );

  const onCopy = async (): Promise<void> => {
    if (!out.result) return;
    try {
      await navigator.clipboard.writeText(out.result);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 900);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <ToolHeader
        toolId="dedupe-lines"
        category="convert"
        description="按行去重：保留首次或末次出现 + 大小写不敏感 + trim + 计数模式（uniq -c 风格）。空行视作普通比较 key（即第二条空行会被去掉）。"
      />

      <section className="card-pad flex flex-wrap items-center gap-3 text-[12px] text-ink-muted">
        <label className="inline-flex cursor-pointer items-center gap-1.5">
          <input
            type="checkbox"
            checked={keepFirst}
            onChange={(e) => setKeepFirst(e.target.checked)}
          />
          保留首次出现 ({keepFirst ? '默认' : '保留末次'})
        </label>
        <label className="inline-flex cursor-pointer items-center gap-1.5">
          <input
            type="checkbox"
            checked={caseInsensitive}
            onChange={(e) => setCaseInsensitive(e.target.checked)}
          />
          大小写不敏感
        </label>
        <label className="inline-flex cursor-pointer items-center gap-1.5">
          <input
            type="checkbox"
            checked={trim}
            onChange={(e) => setTrim(e.target.checked)}
          />
          自动 trim 每行
        </label>
        <label className="inline-flex cursor-pointer items-center gap-1.5">
          <input
            type="checkbox"
            checked={emitCount}
            onChange={(e) => setEmitCount(e.target.checked)}
          />
          计数模式 (uniq -c)
        </label>
      </section>

      <section className="card-pad flex flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            输入
          </span>
          <span className="text-[11px] text-ink-dim">
            {input ? input.split(/\r?\n/).length : 0} 行
          </span>
        </header>
        <textarea
          className="input min-h-[140px] resize-y font-mono text-[13px]"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          spellCheck={false}
          placeholder="每行一项，下方实时显示去重结果"
        />
      </section>

      <section className="card-pad flex min-h-0 flex-1 flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            输出
          </span>
          <div className="flex items-center gap-2 text-[11px] text-ink-dim">
            <span>{out.uniqueCount} 行</span>
            {out.removedCount > 0 && (
              <span className="badge">去重 {out.removedCount} 条</span>
            )}
            <button
              type="button"
              onClick={onCopy}
              disabled={!out.result}
              className="btn-ghost px-2 py-0.5"
              title="复制结果"
            >
              {copied ? (
                <>
                  <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                  已复制
                </>
              ) : (
                <>
                  <ClipboardCopy className="h-3 w-3" />
                  复制
                </>
              )}
            </button>
          </div>
        </header>
        <textarea
          className="input flex-1 resize-none font-mono text-[13px]"
          value={out.result}
          readOnly
          spellCheck={false}
        />
      </section>
    </div>
  );
}
